import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PurchaseOrderItemsAutoComplete } from "@/features/ui/autoComplete/purchaseOrderItems/purchaseOrderItems-autoComplete";
import { BaseDateInput } from "@/features/ui/dateInput/BaseDateInput";
import { handleErrorResponse } from "@/utils/errorMessage";
import { Button, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  DefaultApiWmsOutgoingPostRequest,
  WorkLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Date: BaseDateInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  ItemAutoComplete: ItemAutoComplete,
});

export const OutgoingForm = (params: { formatterProps: any }) => {
  const { closeModal } = useModal();
  const { formatterProps } = params;
  const queryClient = useQueryClient();

  const { mutate: outgoingMutate } = useMutation(
    (params: DefaultApiWmsOutgoingPostRequest) =>
      mutateInventories
        .outgoing(params)
        .mutationFn(params as DefaultApiWmsOutgoingPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        closeModal(form.values);
        customAlert("재고 출고에 성공하였습니다.", "재고 출고 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "재고 출고" }),
          "재고 출고 실패",
          "red",
        );
      },
    },
  );
  const form = useForm<Partial<WorkLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner>>({
    initialValues: {
      itemCode: formatterProps.itemCode,
      locationCode: formatterProps.locationCode,
      quantity: undefined,
      lotId: formatterProps.lotId,
      purchaseOrderItemId: undefined,
      lotInfo: {
        name: formatterProps.lotName,
        expiration:
          formatterProps.lotExpiration !== null
            ? new Date(formatterProps.lotExpiration).toISOString()
            : null,
      },
    },
  });

  const onSubmit = () => {
    try {
      outgoingMutate({
        workLogsWorkLogIdCancelEndPut200ResponseCancelProductionInner: {
          itemCode: formatterProps.itemCode,
          purchaseOrderItemId: Number(form.values.purchaseOrderItemId),
          locationCode: formatterProps.locationCode,
          quantity: form.values.quantity
            ? form.values.quantity
            : (formatterProps.quantity as string),
          lotId: Number(formatterProps.lotId || form.values.lotId),
          lotInfo: {
            name:
              formatterProps.lot.name ||
              (form.values.lotInfo && form.values.lotInfo.name ? form.values.lotInfo.name : null),
            expiration: form.values.lotInfo?.expiration
              ? new Date(form.values.lotInfo.expiration).toISOString()
              : null,
          },
          actionFrom: "WEB",
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: "재고 출고" }),
        "재고 출고 실패",
        "red",
      );
    }
  };

  return (
    <>
      <Create.Wrapper>
        <Create.ItemAutoComplete
          disabled
          label="품목코드"
          withAsterisk
          {...form.getInputProps("itemCode")}
        />
        <LocationAutoComplete
          disabled
          label="로케이션코드"
          withAsterisk
          {...form.getInputProps("locationCode")}
        />
        <PurchaseOrderItemsAutoComplete
          label="납품 할 수주 선택"
          placeholder="수주 선택"
          w={"100%"}
          itemCodes={formatterProps?.itemCode}
          status={true}
          {...form.getInputProps("purchaseOrderItemId")}
        />
        <Create.Input
          label="수량"
          withAsterisk
          type="number"
          {...form.getInputProps("quantity")}
          defaultValue={formatterProps?.quantity}
        />
        <Create.Input disabled label="LOT ID" {...form.getInputProps("lotId")} />
        <Create.Input disabled label="로트명" {...form.getInputProps("lotInfo.name")} />
        <Create.Date
          disabled
          label="만료일"
          minDate={new Date()}
          valueFormat="YYYY-MM-DD HH:mm:ss"
          value={
            form.values.lotInfo?.expiration ? new Date(form.values.lotInfo.expiration) : undefined
          }
        />
        <Create.ButtonBox>
          <Create.Button color="gray" onClick={closeModal}>
            취소
          </Create.Button>
          <Create.Button onClick={onSubmit}>재고 출고</Create.Button>
        </Create.ButtonBox>
      </Create.Wrapper>
    </>
  );
};
